import React, { useEffect, useState } from 'react';

import { Box, Button, Loader } from '@mantine/core';
import { IconAlertCircle, IconArrowRight, IconCircleCheck } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { FullScreenAlert } from 'components/FullScreenAlert';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthService } from 'services/auth-service';
import { REDIRECT_SEARCH_PARAM_KEY, ROUTE_LINKS } from 'shared/constants/route-links';
import { extractRedirectPathFromQueryParams } from 'shared/utils/navigation';

enum VerificationResult {
  SUCCESS = 'SUCCESS',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  INVALID_CODE = 'INVALID_CODE',
  EXPIRED_CODE = 'EXPIRED_CODE',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  LOADING = 'LOADING'
}

export function SignUpVerificationPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const [verificationResult, setVerificationResult] = useState<VerificationResult>(VerificationResult.LOADING);
  const { email, confirmationCode } = queryString.parse(location.search);
  const navigate = useNavigate();

  const verifySignUpMutation = useMutation({
    mutationFn: AuthService.verifySignUp,
    onSuccess: () => setVerificationResult(VerificationResult.SUCCESS),
    onError: (error: AxiosError) => {
      const errorCode = error.response?.status;
      switch (errorCode) {
        case HttpStatusCode.NotFound:
          setVerificationResult(VerificationResult.USER_NOT_FOUND);
          break;
        case HttpStatusCode.Gone:
          setVerificationResult(VerificationResult.EXPIRED_CODE);
          break;
        case HttpStatusCode.BadRequest:
          setVerificationResult(VerificationResult.INVALID_CODE);
          break;
        default:
          setVerificationResult(VerificationResult.UNKNOWN_ERROR);
      }
    }
  });

  useEffect(() => {
    if (!email || !confirmationCode || typeof email !== 'string' || typeof confirmationCode !== 'string') {
      setVerificationResult(VerificationResult.UNKNOWN_ERROR);
      return;
    }
    verifySignUpMutation.mutate({ email: decodeURIComponent(email), confirmationCode });
  }, []);

  const navigateToLogin = () => {
    const redirect = extractRedirectPathFromQueryParams(queryString.parse(location.search));
    const loginUrl = queryString.stringifyUrl({
      url: ROUTE_LINKS.login,
      query: redirect ? { [REDIRECT_SEARCH_PARAM_KEY]: redirect } : {}
    });
    navigate(loginUrl);
  };

  return (
    <FullScreenAlert
      header={t('AUTH.VERIFICATION.TITLE')}
      cardMinWidth={300}
      body={
        <Box mt="md">
          {verificationResult === VerificationResult.SUCCESS && (
            <IconCircleCheck style={{ width: 60, height: 60 }} color="green" />
          )}
          {verificationResult === VerificationResult.LOADING && <Loader size={50} />}
          {verificationResult !== VerificationResult.SUCCESS && verificationResult !== VerificationResult.LOADING && (
            <IconAlertCircle style={{ width: 60, height: 60 }} color="red" />
          )}
        </Box>
      }
      notice={t(`AUTH.VERIFICATION.${verificationResult}`)}
      cta={
        verificationResult === VerificationResult.SUCCESS ? (
          <Button fullWidth onClick={navigateToLogin} rightIcon={<IconArrowRight />}>
            {t('AUTH.SIGN_IN')}
          </Button>
        ) : undefined
      }
    />
  );
}
